import { alpha, type Theme } from '@mui/material/styles';

/**
 * The designer's own look. The canvas is drawn as the checkpoint window an inspector sees,
 * and kept quiet: no borders around fields or groups, logic shown as indentation and a line
 * of muted text, chrome only on hover or selection.
 */

/** Accent for logic: the branch labels and conditions that tie a follow-up to its trigger. */
export const LOGIC_COLOR = '#a78bfa';
export const WARN_COLOR = '#f5a524';

export const designerLayoutStyles = {
  display: 'grid',
  gridTemplateColumns: { xs: '1fr', lg: 'minmax(0, 1fr) 400px' },
  gap: 2,
  alignItems: 'start',
};

export const toolbarStyles = {
  px: 2,
  py: 1.25,
  gap: 1.5,
  alignItems: 'center',
  flexWrap: 'wrap',
  borderBottom: '1px solid',
  borderColor: 'divider',
};

/** The device: one quiet frame, nothing inside it framed again. */
export const deviceStyles = {
  maxWidth: 640,
  mx: 'auto',
  my: 2.5,
  px: 2.5,
  py: 2,
  borderRadius: 2,
  border: '1px solid',
  borderColor: 'divider',
  backgroundColor: (theme: Theme) => alpha(theme.palette.common.black, 0.22),
};

/** One field on the canvas: no border, a soft background on hover. */
export const fieldCardStyles = {
  px: 1.25,
  py: 1,
  borderRadius: 1.5,
  cursor: 'pointer',
  transition: 'background-color 120ms ease',
  '&:hover': { backgroundColor: 'action.hover' },
  '&:hover .designer-actions': { opacity: 1 },
};

/** Selected: a tint and a thin accent edge, not a box. */
export const fieldCardSelectedStyles = {
  backgroundColor: (theme: Theme) => alpha(theme.palette.primary.main, 0.1),
  boxShadow: (theme: Theme) => `inset 2px 0 0 ${theme.palette.primary.main}`,
  '&:hover': { backgroundColor: (theme: Theme) => alpha(theme.palette.primary.main, 0.12) },
  '& .designer-actions': { opacity: 1 },
};

/**
 * A field's or group's actions, at the end of its first row. Their space is always kept and
 * only their opacity changes, so selecting something never moves the form.
 */
export const cardActionsStyles = {
  opacity: 0,
  transition: 'opacity 120ms ease',
  '&:focus-within': { opacity: 1 },
  '& .MuiIconButton-root': { p: 0.5, color: 'text.secondary', fontSize: 16 },
};

/** Drag handle, only there while the block is hovered. */
export const handleStyles = {
  position: 'absolute',
  left: -22,
  top: 12,
  display: 'inline-flex',
  fontSize: 16,
  color: 'text.disabled',
  cursor: 'grab',
  touchAction: 'none',
  opacity: 0,
  transition: 'opacity 120ms ease',
  '&:hover': { color: 'text.secondary' },
};

export const sortableHoverStyles = {
  '&:hover > .drag-handle': { opacity: 1 },
};

/** Follow-ups: indented under their trigger, one faint line to hold them together. */
export const branchStyles = {
  ml: 2,
  pl: 1.5,
  borderLeft: `1px solid ${alpha(LOGIC_COLOR, 0.28)}`,
};

/** The answer a run of follow-ups belongs to: plain text, no pill. */
export const branchLabelStyles = {
  display: 'block',
  pt: 0.75,
  px: 1.25,
  fontSize: 12,
  fontWeight: 600,
  color: LOGIC_COLOR,
};

/** "Asked when …" above a conditional field that is not drawn under its trigger. */
export const conditionLineStyles = {
  display: 'block',
  mb: 0.5,
  fontSize: 12,
  fontWeight: 500,
  color: alpha(LOGIC_COLOR, 0.9),
};

export const warningLineStyles = {
  display: 'block',
  mt: 0.5,
  fontSize: 12,
  color: WARN_COLOR,
};

/** A group is a section heading, the way the device draws it, not a box. */
export const groupHeaderStyles = {
  mt: 2.5,
  px: 1.25,
  py: 0.75,
  borderRadius: 1.5,
  cursor: 'pointer',
  transition: 'background-color 120ms ease',
  '&:hover': { backgroundColor: 'action.hover' },
  '&:hover .designer-actions': { opacity: 1 },
};

export const inheritedRegionStyles = {
  mb: 1.5,
  py: 0.5,
  borderRadius: 1.5,
  backgroundColor: (theme: Theme) => alpha(theme.palette.primary.main, 0.05),
};

/** Quiet text buttons for adding things: no dashed boxes. */
export const addButtonStyles = {
  textTransform: 'none',
  color: 'text.secondary',
  fontWeight: 500,
  '&:hover': { color: 'primary.light', backgroundColor: 'transparent' },
};

export const inspectorStyles = {
  position: { lg: 'sticky' },
  top: { lg: 16 },
  maxHeight: { lg: 'calc(100vh - 32px)' },
  overflowY: 'auto',
};

export const sectionTitleStyles = {
  fontSize: 11,
  fontWeight: 700,
  letterSpacing: '.06em',
  textTransform: 'uppercase',
  color: 'text.secondary',
};

export const conditionBoxStyles = {
  p: 1.25,
  borderRadius: 1,
  backgroundColor: alpha(LOGIC_COLOR, 0.06),
};

export const segmentedStyles = {
  '& .MuiToggleButton-root': {
    textTransform: 'none',
    fontWeight: 600,
    px: 1.25,
    py: 0.375,
  },
};

/** A design-mode input: the shape of the device's control, without its border. */
export const fakeInputStyles = {
  display: 'flex',
  alignItems: 'center',
  gap: 1,
  minHeight: 34,
  px: 1.25,
  borderRadius: 1,
  fontSize: 14,
  backgroundColor: (theme: Theme) => alpha(theme.palette.common.white, 0.05),
};

/** A label shown in another language because this one has no translation yet. */
export const fallbackTextStyles = {
  opacity: 0.5,
  fontStyle: 'italic',
};
